﻿import { StringUtils } from './ObjectUtils.js';

/** An instance if InputValidator is associated with an input html element and contains all our
 * needed code in order to use a combination of built-in and custom validation features. */
export class InputValidator {
  /**
   * Creates an InputValidator.
   * @param {Element} input The input element associated with this validator object.
   * @param {Function} onGetMessage Callback for getting the validation error message. Passes the
   * input element as the only parameter. During validation, if onGetMessage is unassigned, then
   * the built-in input.validationMessage is used instead.
   * @param {Function} onCustomValidate Callback for custom validation. During validation,
   * if unassigned, input.checkValidity() is called instead.
   */
  constructor(input, onGetMessage, onCustomValidate) {
    this.input = input;
    this.feedbackElement = this.findFeedbackElement();
    this.onGetMessage = onGetMessage;
    this.onCustomValidate = onCustomValidate;
    this.setupEvents();
  }

  findFeedbackElement() {
    let element = document.querySelector(`#${this.input.id} ~ div.invalid-feedback`);
    /* The code below was written when we thought we needed to support invalid-feedback elements
     * being positioned at one level above the input element (i.e. an "uncle" instead of a "sibling"),
     * but currently we don't have a need for it. Anyway, we'll leave the code here just in case.
     * 7/23/2019. */
    if (!element) {
      element = this.input.parentElement;
      while (element && !element.classList.contains('invalid-feedback')) {
        element = element.nextElementSibling;
      }
    }
    return element;
  }

  /**
   * Adds an handler for the input element's input event. The handler will ensure input.validationMessage
   * is always up to date. Assigns the handler to a property so that we can properly clean up afterwards
   * in the teaddownEvents method. Also see the dispose() method for the recommended way to manually
   * trigger cleanup.
   */
  setupEvents() {
    this.inputEventHandler = event => this.validate(event.target);
    this.input.addEventListener('input', this.inputEventHandler);
  }

  teardownEvents() {
    this.input.removeEventListener('input', this.inputEventHandler);
  }

  dispose() {
    this.teardownEvents();
  }

  /**
   * Returns true if this is a custom validator. */
  isCustom() {
    return this.onCustomValidate != null; // True if assigned and not null
  }

  updateFeedbackElement() {
    if (this.feedbackElement) {
      this.feedbackElement.innerText = this.input.validationMessage;
    }
  }

  /** Calls onGetMessage to get any custom custom validation message. If unassigned, the built-in
   * input.validationMessage is used instead. Finally assigns the message to the input element by
   * calling setCustomValidity(msg) and calls this.updateFeedbackElement to update the html UI.*/
  updateValidationMessage() {
    let msg = this.onGetMessage ? this.onGetMessage(this.input) : null;
    msg = msg || this.input.validationMessage;
    this.input.setCustomValidity(msg);
    this.updateFeedbackElement();
  }

  /**
   * Updates the input's custom validity message by using the onCustomValidate callback. This method
   * can only be used if isCustom() is true. Also calls this.updateValidationMessage() to get any
   * custom messages and to update our html UI.
   * @param {any} resetMessageFirst If true or unassigned, then input.setCustomValidity('') is called.
   */
  checkCustomValidity(resetMessageFirst) {
    if (!this.isCustom()) {
      throw new Error('This validator is not custom');
    }
    resetMessageFirst = resetMessageFirst || typeof resetMessageFirst === 'undefined';
    if (resetMessageFirst) {
      this.input.setCustomValidity('');
    }
    if (this.onCustomValidate(this.input)) {
      return true;
    }
    this.updateValidationMessage();
    return false;
  }

  /**
   * Async helper method for validating the input element's current value. No return value. Is
   * normally assigned to the input's onInput event, but can also be called whenever you need to do
   * the validation manually.
   */
  validate() {
    this.input.setCustomValidity('');
    setTimeout(() => {
      if (this.isCustom()) {
        this.checkCustomValidity(/*resetMessageFirst:*/ false);
      } else if (!this.input.checkValidity()) {
        this.updateValidationMessage();
      }
    }, 0);
  }
}

/** Contains a list of InputValidators for easy management of all the html form input elements for
 * which we require validation. */
export class FormValidator {
  constructor() {
    this.validators = [];
  }

  indexOfInput(input) {
    for (var i = 0; i < this.validators.length; i++) {
      if (this.validators[i].input === input) {
        return i;
      }
    }
    return -1;
  }

  add(input, onGetMessage, onCustomValidate) {
    const validator = new InputValidator(input, onGetMessage, onCustomValidate);
    this.validators.push(validator);
    return validator;
  }

  /**
   * Removes the validator from our list, and optionally disposes it.
   * @param {Element} input The input element whose validator will be removed.
   * @param {bool} dispose If true, or unassigned, disposes the validator after removal.
   */
  remove(input, dispose) {
    let validator = null;
    const index = this.indexOfInput(input);
    if (index > -1) {
      validator = this.validators[index];
      this.validators.splice(index, 1);
      dispose = dispose || typeof dispose === 'undefined';
      if (dispose) {
        validator.dispose();
      }
    }
    return validator;
  }

  /** Calls the checkCustomValidity method on each custom validator. */
  checkCustomValidity() {
    let isValid = true;
    this.validators.forEach(validator => {
      if (validator.isCustom()) {
        isValid = validator.checkCustomValidity() && isValid;
      }
    });
    return isValid;
  }

  /** Updates the validation feedback element for each validator. */
  updateFeedbackElements() {
    this.validators.forEach(validator => {
      validator.updateFeedbackElement();
    });
  }
}

class FieldValidator {
  constructor() {
    this.isRequired = false;
    this.regEx = null; // Should be set by subclass constructor
  }

  validate(value) {
    if (StringUtils.isNullOrEmpty(value)) {
      return !this.isRequired; // return true if not required, false if required
    } else {
      return this.regEx.test(value);
    }
  }
}

export class NorwegianPostalCodeValidator extends FieldValidator {
  constructor() {
    super();
    this.regEx = /^\d{4}$/;
  }
}

export class PhoneValidator extends FieldValidator {
  constructor() {
    super();
    this.stripBlanksRegEx = / /g;
  }

  static createValidator(countryCode) {
    switch (countryCode.toUpperCase()) {
      case 'NL':
        return new DutchPhoneValidator();
      case 'NO':
        return new NorwegianPhoneValidator();
      default:
        throw new Error(`Unable to create phone validator due to unknown/unassigned country code: "${countryCode}"`);
    }
  }

  /**
   * Virtual method to be overridden by subclasses. Returns true/false.
   */
  isLocalRegionPhoneNo(phoneNo) {
    return false;
  }

  /**
   * This method uses the phoneNoRegEx property to validate the phone number. Returns true if
   * validated. Before validating all blanks are removed from the number. Descendants may override
   * the method if they have further validation requirements.
   */
  validateLocalRegionPhoneNo(phoneNo) {
    phoneNo = phoneNo.replace(this.stripBlanksRegEx, '');
    return this.regEx.test(phoneNo);
  }

  /**
   * Virtual method to be overridden by subclasses to perform country/regional validation of the
   * phone number. This base method always returns true.
   */
  validateExternalRegionPhoneNo(phoneNo) {
    /* For external libraries doing internation phone number validation/lookup/parsing, see here:
     * https://github.com/google/libphonenumber (Javascript, etc.)
     * https://github.com/twcclegg/libphonenumber-csharp (C# port of the above) */
    return true;
  }

  /**
   * This method delegates to isLocalRegionPhoneNo(), validateLocalRegionPhoneNo(), and
   * validateExternalRegionPhoneNo() to perform validation of the phoneNo. Method can be overridden
   * if special validation is required. Returns true if validation passes.
   */
  validate(phoneNo) {
    if (StringUtils.isNullOrEmpty(phoneNo)) {
      return !this.isRequired; // return true if not required, false if required
    } else if (phoneNo[0] !== '+' || this.isLocalRegionPhoneNo(phoneNo)) {
      return this.validateLocalRegionPhoneNo(phoneNo);
    } else {
      return this.validateExternalRegionPhoneNo(phoneNo);
    }
  }
}

export class DutchPhoneValidator extends PhoneValidator {
  constructor() {
    super();

    /* Dutch domestic pattern(simplified): ^ (?=^.{ 10, 11 } $) 0\d * -?\d * $
     * Dutch pattern, more complete, but only allows one space char. Ideally we would strip extra
     * spaces first then test(using validation):
     * (^\+[0-9]{2}|^\+[0-9]{2}\(0\)|^\(\+[0-9]{2}\)\(0\)|^00[0-9]{2}|^0)([0-9]{9}$|[0-9\-\s]{10}$)
     */
    this.regEx = /(^\+[0-9]{2}|^\+[0-9]{2}\(0\)|^\(\+[0-9]{2}\)\(0\)|^00[0-9]{2}|^0)([0-9]{9}$|[0-9\-\s]{10}$)/;
  }

  /**
   * Returns true if this is an international number for the local region (e.g. starts with +31).
   */
  isLocalRegionPhoneNo(phoneNo) {
    return phoneNo.startsWith('+31');
  }
}

export class NorwegianPhoneValidator extends PhoneValidator {
  constructor() {
    super();

    /* Taken from Stack Overflow https://stackoverflow.com/questions/34001939/regular-expression-for-norwegian-numbers
     * Also see https://no.wikipedia.org/wiki/Telefonnummer and https://en.wikipedia.org/wiki/Telephone_numbers_in_Norway
     */
    this.regEx = /^(0047|\+47|47)?[2-9]\d{7}$/;
  }

  /**
   * Returns true if this is an international number for the local region (e.g. starts with +31).
   */
  isLocalRegionPhoneNo(phoneNo) {
    return phoneNo.startsWith('+47');
  }
}
